import { useState } from 'react';
import {
  Github,
  GitBranch,
  GitCommit,
  Upload,
  Download,
  RefreshCw,
  ExternalLink,
  User,
  Calendar,
  Loader,
  AlertCircle,
  X
} from 'lucide-react';
import { useGitHubIntegration } from '@/hooks/use-github-integration';

interface GitHubIntegrationPanelProps {
  agentId?: string;
  isOpen: boolean;
  onClose: () => void;
}

export function GitHubIntegrationPanel({ agentId, isOpen, onClose }: GitHubIntegrationPanelProps) {
  const [commitMessage, setCommitMessage] = useState('');
  const [showCommitDialog, setShowCommitDialog] = useState(false);
  
  const {
    connection,
    isLoading,
    error,
    commits,
    isLoadingCommits,
    isPushing,
    isPulling,
    connectToGitHub,
    selectRepository,
    pushToGitHub,
    pullFromGitHub,
    disconnect
  } = useGitHubIntegration(agentId);

  const handlePush = async () => {
    if (!commitMessage.trim()) return;
    await pushToGitHub(commitMessage);
    setCommitMessage('');
    setShowCommitDialog(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Github className="size-5" />
            <h2 className="text-lg font-semibold">GitHub Integration</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="flex h-[calc(90vh-4rem)]">
          {/* Sidebar */}
          <div className="w-80 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
            {!connection.isConnected ? (
              <div className="space-y-4">
                <div className="text-center">
                  <Github className="size-12 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-lg font-medium mb-2">Connect to GitHub</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Connect your GitHub account to push, pull, and manage your repositories.
                  </p>
                  <button
                    onClick={connectToGitHub}
                    disabled={isLoading}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isLoading ? (
                      <Loader className="size-4 animate-spin" />
                    ) : (
                      <Github className="size-4" />
                    )}
                    {isLoading ? 'Connecting...' : 'Connect GitHub'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* User Info */}
                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <User className="size-8 text-gray-600" />
                  <div>
                    <div className="font-medium">{connection.username}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Connected</div>
                  </div>
                  <button
                    onClick={disconnect}
                    className="ml-auto text-sm text-red-600 hover:text-red-700"
                  >
                    Disconnect
                  </button>
                </div>

                {/* Repositories */}
                <div>
                  <h4 className="font-medium mb-2">Repositories</h4>
                  <div className="space-y-2">
                    {connection.repositories.map((repo) => (
                      <button
                        key={repo.id}
                        onClick={() => selectRepository(repo)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          connection.selectedRepository?.id === repo.id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <GitBranch className="size-4" />
                          <span className="font-medium">{repo.name}</span>
                          {repo.private && (
                            <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1 rounded">Private</span>
                          )}
                        </div>
                        {repo.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">{repo.description}</p>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                {connection.selectedRepository && (
                  <div className="space-y-2">
                    <button
                      onClick={() => setShowCommitDialog(true)}
                      disabled={isPushing}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isPushing ? (
                        <Loader className="size-4 animate-spin" />
                      ) : (
                        <Upload className="size-4" />
                      )}
                      {isPushing ? 'Pushing...' : 'Push Changes'}
                    </button>
                    
                    <button
                      onClick={pullFromGitHub}
                      disabled={isPulling}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isPulling ? (
                        <Loader className="size-4 animate-spin" />
                      ) : (
                        <Download className="size-4" />
                      )}
                      {isPulling ? 'Pulling...' : 'Pull Changes'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                  <AlertCircle className="size-4" />
                  <span className="text-sm">{error}</span>
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 p-4 overflow-y-auto">
            {connection.selectedRepository ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium">{connection.selectedRepository.name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {connection.selectedRepository.description}
                    </p>
                  </div>
                  <a
                    href={connection.selectedRepository.html_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                  >
                    <ExternalLink className="size-4" />
                    View on GitHub
                  </a>
                </div>

                {/* Commit History */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium">Recent Commits</h4>
                    <button
                      onClick={() => {/* reload commits */}}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      <RefreshCw className="size-4" />
                    </button>
                  </div>

                  {isLoadingCommits ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader className="size-6 animate-spin" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {commits.map((commit) => (
                        <div
                          key={commit.sha}
                          className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                        >
                          <div className="flex items-start gap-3">
                            <GitCommit className="size-4 mt-1 text-gray-500" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">{commit.message}</span>
                                <a
                                  href={commit.html_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:text-blue-700"
                                >
                                  {commit.sha.slice(0, 7)}
                                </a>
                              </div>
                              <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                                <span className="flex items-center gap-1">
                                  <User className="size-3" />
                                  {commit.author.name}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="size-3" />
                                  {new Date(commit.author.date).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <GitBranch className="size-12 mx-auto mb-4" />
                  <p>Select a repository to view details and commit history</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Commit Dialog */}
        {showCommitDialog && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-medium mb-4">Commit Changes</h3>
              <textarea
                value={commitMessage}
                onChange={(e) => setCommitMessage(e.target.value)}
                placeholder="Enter commit message..."
                className="w-full h-24 p-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none"
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => setShowCommitDialog(false)}
                  className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePush}
                  disabled={!commitMessage.trim() || isPushing}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg disabled:opacity-50"
                >
                  {isPushing ? 'Pushing...' : 'Commit & Push'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
